import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '/api';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser]           = useState(null);
  const [sessions, setSessions]   = useState([]);
  const [favorites, setFavorites] = useState({});
  const [progress, setProgress]   = useState({});
  const [loading, setLoading]     = useState(true);
  const [authError, setAuthError] = useState('');
  const [textSize, setTextSizeState] = useState(
    () => localStorage.getItem('gc_text_size') || 'normal'
  );

  const token = () => localStorage.getItem('gc_token');
  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token()}`,
  });

  // ── Aplicar tamaño de texto en <html data-text-size="..."> ───────
  useEffect(() => {
    document.documentElement.setAttribute('data-text-size', textSize);
  }, [textSize]);

  function setTextSize(size) {
    if (!['small', 'normal', 'large'].includes(size)) return;
    localStorage.setItem('gc_text_size', size);
    setTextSizeState(size);
  }

  // ── Cargar estado persistido de favoritos y progreso ───
  useEffect(() => {
    const savedFavs = JSON.parse(localStorage.getItem('gc_favorites') || '{}');
    const savedProg = JSON.parse(localStorage.getItem('gc_progress')  || '{}');
    setFavorites(savedFavs);
    setProgress(savedProg);
  }, []);

  // ── Auto-login si hay token ───
  useEffect(() => {
    const t = token();
    if (t) fetchMe();
    else setLoading(false);
  }, []);

  async function fetchMe() {
    try {
      const res = await fetch(`${API}/auth/me`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setUser(data);
      await fetchSessions();
    } catch {
      localStorage.removeItem('gc_token');
    } finally {
      setLoading(false);
    }
  }

  async function fetchSessions() {
    try {
      const res = await fetch(`${API}/sessions`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSessions(data);
    } catch {
      setSessions([]);
    }
  }

  async function login(email, password) {
    setAuthError('');
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) { setAuthError(data.message || 'Error al ingresar'); return false; }
      localStorage.setItem('gc_token', data.token);
      setUser(data.user);
      await fetchSessions();
      return true;
    } catch {
      setAuthError('Sin conexión. Intentá de nuevo.');
      return false;
    }
  }

  function logout() {
    localStorage.removeItem('gc_token');
    setUser(null);
    setSessions([]);
  }

  function toggleFavorite(sessionId) {
    setFavorites(prev => {
      const next = { ...prev, [sessionId]: !prev[sessionId] };
      if (!next[sessionId]) delete next[sessionId];
      localStorage.setItem('gc_favorites', JSON.stringify(next));
      fetch(`${API}/sessions/${sessionId}/favorite`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ favorite: !!next[sessionId] }),
      }).catch(() => {});
      return next;
    });
  }

  const saveProgress = useCallback((sessionId, pct, trackIndex, position) => {
    const entry = { pct, trackIndex, position, updatedAt: Date.now() };
    setProgress(prev => {
      const next = { ...prev, [sessionId]: entry };
      localStorage.setItem('gc_progress', JSON.stringify(next));
      return next;
    });
    fetch(`${API}/sessions/${sessionId}/progress`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ pct, trackIndex, position }),
    }).catch(() => {});
  }, []);

  function recordPlay(sessionId) {
    fetch(`${API}/sessions/${sessionId}/play`, {
      method: 'POST',
      headers: authHeaders(),
    }).catch(() => {});
  }

  return (
    <AppContext.Provider value={{
      user, sessions, favorites, progress,
      loading, authError,
      login, logout,
      toggleFavorite, saveProgress, recordPlay,
      fetchSessions,
      textSize, setTextSize,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
